'use client'

import React, { useReducer, useEffect, createContext, useContext } from 'react'
import ModalOverlay from '../components/molecules/Modal/ModalOverlay/ModalOverlay.component'

interface ModalState {
  component: React.ComponentType | null
  modalProps: Record<string, any> | null 
  isOpen: boolean
  hasAnnouncementBar: boolean
  animateChild?: boolean
  toggleAnnouncementBar: (status: boolean) => void
  showModal: (component: React.ComponentType, modalProps?: Record<string, any>, animateChild?: boolean) => void
  closeModal: () => void,
  state?: ModalState | undefined,
}

interface ModalAction {
  type: 'open' | 'close' | 'animate' | 'toggleAnnouncementBar'
  payload?: any
}

export const ModalContext = createContext<ModalState | undefined>(undefined)

import { useLoading } from './Loading.context'

const reducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        isOpen: true,
        component: action.payload.component,
        modalProps: action.payload.modalProps
      }
    case 'close': 
      return {
        ...state,
        isOpen: false,
        animateChild: false,
        component: null,
        modalProps: null
      }
    case 'animate': 
      return {
        ...state,
        animateChild: true
      }
    case 'toggleAnnouncementBar':
      return {
        ...state,
        hasAnnouncementBar: action.payload,
      }
    default:
      return { ...state }
  }
}

interface ModalProviderProps {
  children: React.ReactNode
}

const ModalProvider = ({ children }: ModalProviderProps) => {
  const initialState: ModalState = {
    component: null,
    modalProps: null,
    isOpen: false,
    hasAnnouncementBar: true,
    toggleAnnouncementBar: (status) => {
      dispatch({ type: 'toggleAnnouncementBar', payload: status })
    },
    showModal: (component, modalProps, animateChild = true) => {
      dispatch({ type: 'open', payload: { component, modalProps } })
      if (animateChild) setTimeout(() => {
        dispatch({ type: 'animate' })
      }, 200)
    },
    closeModal: () => {
      dispatch({ type: 'close' })
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState)
  const { isLoading } = useLoading()

  const onkeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      dispatch({ type: 'close' })
    }
  }

  useEffect(() => {
    if (state.component) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', onkeydown)

    } else {
      document.body.style.overflow = 'auto'
      document.removeEventListener('keydown', onkeydown)
    }
  }, [state.component])

  useEffect(() => {
    if (isLoading) {
      state.closeModal()
    }
  }, [isLoading])

  return (
    <ModalContext.Provider
      value={{
        ...state,
        toggleAnnouncementBar: state.toggleAnnouncementBar,
        showModal: state.showModal,
        closeModal: state.closeModal,
      }}
    >
      {children}
      <ModalOverlay
        Component={state.component}
        modalProps={state.modalProps || {}}
        isOpen={state.isOpen}
        animateChild={state.animateChild}
        closeModal={state.closeModal}
      />
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

export default ModalProvider
